import {
	init,
	setSessionId,
	shutdown,
	getSessionReplayProperties,
} from '@amplitude/session-replay-browser';
import type AnalyticsClient from '@atlassiansox/analytics-web-client';
import type { ScreenEventPayload } from '@atlassiansox/analytics-web-client';
import { allFeatureFlagsAccessed } from '@atlaskit/react-ufo/feature-flags-accessed';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { getAnalyticsWebClient } from '@atlassian/jira-product-analytics-web-client';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { addResourcesListener } from '@atlassian/react-resource-router';
import {
	CONSENT_BANNER_USER_PREFERENCES,
	CONSENT_BANNER_USER_PREFERENCES_KEY,
} from '@atlassian/jira-router-resources-cookie-consent/src/constants.tsx';
import type {
	ConsentHubResponse,
	PrefetchedConsentPreferenceData,
} from '@atlassian/browser-storage-controls';
import { performGetRequest } from '@atlassian/jira-fetch/src/utils/requests.tsx';
import type { AmplitudeSessionReplayType } from './types.tsx';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sendErrorAnalytics = (id: string, message: string, error?: any) => {
	fireErrorAnalytics({
		meta: {
			id,
			packageName: 'jira-amplitude-session-replay',
		},
		attributes: {
			message,
		},
		error,
	});
};

const buildSRInitEventPayload = () => {
	const sessionReplayProperties = getSessionReplayProperties();

	// Convert the Map of accessed feature flags into an object, keeping only flags with values not falsy
	const accessedFeatureFlags = Object.fromEntries(
		[...allFeatureFlagsAccessed].filter(([, value]) => value),
	);

	const sessionReplayEventPayload: ScreenEventPayload = {
		name: 'amplitudeSR initialized',
		attributes: {
			...sessionReplayProperties,
			...accessedFeatureFlags,
		},
	};

	return sessionReplayEventPayload;
};

const onSessionIdChange = (analyticsClient: AnalyticsClient) => {
	setSessionId(analyticsClient.getCurrentSessionId());
};

type ConsentHubKeyType = `${NonNullable<ConsentHubResponse['consents']>[number]['key']}`;
const AnalyticsConsentKey: ConsentHubKeyType = 'performanceCookies';

const loadSRWithConsent = async () => {
	const clearResourcesListener = addResourcesListener(async (resourcesState) => {
		clearResourcesListener();

		// consent resource definition here jira/src/packages/router-resources/cookie-consent/src/index.tsx
		let cookieConsentsData: PrefetchedConsentPreferenceData =
			resourcesState?.data?.[CONSENT_BANNER_USER_PREFERENCES]?.[CONSENT_BANNER_USER_PREFERENCES_KEY]
				?.data;

		// this means that user has provided consent via the cookie banner
		// but we do not know if the user has provided consent for analytics
		// so we need to check via ConsentHub API
		// this is not done by default to prevent unnecessary API calls
		if (!cookieConsentsData || !('consents' in cookieConsentsData)) {
			try {
				cookieConsentsData = await performGetRequest<ConsentHubResponse>(
					'/gateway/api/consenthub/session/user/consents/cookies',
				);
			} catch (error) {
				sendErrorAnalytics('loadSRWithConsent', 'Failed to get cookies consent data', error);
			}
		}

		if (
			cookieConsentsData &&
			'consents' in cookieConsentsData &&
			cookieConsentsData.consents &&
			cookieConsentsData.consents.length > 0
		) {
			const hasCookieConsent = cookieConsentsData.consents.some(
				(consent) => consent.key === AnalyticsConsentKey && consent.granted,
			);

			if (hasCookieConsent) {
				loadSR();
			} else {
				sendErrorAnalytics('loadSRWithConsent', 'No analytics cookies consent found');
			}
		}
	});
};

// exporting only for testing purposes
// DO NOT USE THIS FUNCTION OUTSIDE OF TESTS
export const loadSR = async () => {
	try {
		const client = getAnalyticsWebClient();
		if (client && client.getInstance) {
			const analyticsClient = client.getInstance();
			if (analyticsClient && client.isInitialized && client.isInitialized()) {
				// Configure the SDK and begin collecting replays
				const AMPLITUDE_API_KEY = 'eeecdc90c13797e7eb4d432b0ed5041b';

				await init(AMPLITUDE_API_KEY, {
					deviceId: analyticsClient.getAnonymousId(),
					sessionId: analyticsClient.getCurrentSessionId(),
					storeType: 'memory',
					logLevel: fg('jira_amplitude_sr_logs') ? 4 : undefined,
					optOut: false,
					// eslint-disable-next-line jira/ff/inline-expVal-params
					...expVal('jira_amplitude_sr_exp', 'sdkOptions', {}),
				}).promise;

				// fire an screen event as operational event can't be ingested in Amplitude
				// Session replay doesn't require that all events in a session have the [Amplitude] Session Replay ID property, only that one event in the session has it
				// https://amplitude.com/docs/session-replay/session-replay-standalone-sdk#some-sessions-dont-include-the-session-replay-id-property
				analyticsClient.sendScreenEvent(buildSRInitEventPayload());

				client.setUpdateAmplitudeSRClient(() => onSessionIdChange(analyticsClient));
			}
		}
	} catch (error) {
		sendErrorAnalytics('loadSR', 'Failed to initialize amplitude session replay', error);
	}
};

const unloadSR = async () => {
	try {
		await shutdown();
	} catch (error) {
		sendErrorAnalytics('unloadSR', 'Failed to shutdown amplitude session replay', error);
	}
};

const AmplitudeSessionReplay: AmplitudeSessionReplayType = {
	init: loadSRWithConsent,
	stop: unloadSR,
};

export default AmplitudeSessionReplay;
